// Redação de credencial e contato no que os SPAs mandam para fora (PostHog e
// GA4). #795 (auditoria geral 2026-10-03, achados OBS-1 e OBS-6).
//
// Mora aqui, e não em `ui-shared`, pelo mesmo motivo do `posthogKey.ts`: é o
// único pacote comum a app/admin/crm, e o admin e o CRM não dependem de
// `ui-shared` de propósito. Função pura, sem DOM e sem `posthog-js` — os tipos
// abaixo são estruturais para o pacote não ganhar dependência de SDK.

/** O que entra no lugar do valor redigido. Mesmo texto do `sanitizeDetails` do CRM. */
export const TELEMETRY_REDACTED = '[redacted]';

/**
 * Parâmetros de URL cujo VALOR é credencial ou dado pessoal. Comparação
 * case-insensitive, só no nome exato (`email=` casa; `emailVerified=` não).
 *
 * Origem: #795 — 2026-10-03 14:15 +0200 (OBS-1).
 *
 * De onde vem cada um:
 *  - `token`: convite do CRM (`/invite/accept?token=`, a credencial de 14 dias
 *    gerada em `apps/crm-functions/src/lib/invites.ts`) e confirmação de
 *    exclusão do tenant (`/privacy/confirm-tenant-deletion?token=`, `apps/app`);
 *  - `email`: `/login?email=` do `apps/app`;
 *  - `oobCode` / `continueUrl` / `code`: links de ação do Firebase Auth e
 *    retornos OAuth — o `continueUrl` do convite carrega o link COM o token,
 *    e chega como `$referrer` quando o convidado volta da redefinição de senha;
 *  - `phone`: `whatsapp://send?phone=` e afins;
 *  - `attemptId`: o id da página pública `/pay/:attemptId` também pode vir como query;
 *  - os demais (`*_token`, `client_secret`, `secret`, `password`): nomes que
 *    nunca deveriam estar numa URL — se aparecerem, não saem.
 *
 * Usado por: `redactTelemetryText` (abaixo).
 */
export const SENSITIVE_URL_PARAMS: readonly string[] = [
  'token',
  'access_token',
  'id_token',
  'refresh_token',
  'client_secret',
  'secret',
  'password',
  'code',
  'oobCode',
  'continueUrl',
  'email',
  'phone',
  'attemptId',
];

const PARAM_ALTERNATION = SENSITIVE_URL_PARAMS.join('|');

/** `?token=abc` → `?token=[redacted]`. O valor termina em `&`, `#`, aspas ou espaço. */
const PARAM_RE = new RegExp(`([?&#](?:${PARAM_ALTERNATION})=)[^&#"'\\s]*`, 'gi');

/**
 * A mesma coisa dentro de uma URL CODIFICADA num parâmetro (`redirect=…%3Ftoken%3Dabc`).
 * O valor termina no próximo `%26`/`%23` (o `&`/`#` codificado) ou num separador cru.
 */
const ENCODED_PARAM_RE = new RegExp(
  `((?:%3F|%26|%23)(?:${PARAM_ALTERNATION})%3D)(?:(?!%26|%23)[^&#"'\\s])*`,
  'gi',
);

/**
 * `mailto:`/`tel:`/`sms:`/`callto:`/`whatsapp:` — o destino inteiro é dado de
 * contato. Exige um caractere logo depois do `:` para não mexer em rótulo
 * (`Tel: 11 9…` tem espaço e passa intacto); `\b` evita casar dentro de
 * palavra (`Hotel:`).
 */
const CONTACT_SCHEME_RE = /\b(mailto|tel|sms|callto|whatsapp):(?=[^\s"'])[^"'\s]*/gi;

/** `https://wa.me/5511999999999?text=…` → `https://wa.me/[redacted]` (número e mensagem). */
const WHATSAPP_HOST_RE = /\b(wa\.me|(?:api|web)\.whatsapp\.com)\/[^"'\s]*/gi;

/**
 * `/pay/<attemptId>` (e `/:lang/pay/<attemptId>`) do `apps/app`: o id é a
 * capacidade da página pública de pagamento. `success`/`cancelled` são rotas
 * fixas e ficam.
 */
const PAY_PATH_RE = /(\/pay\/)(?!(?:success|cancelled)(?=[/?#"'\s]|$))[^/?#"'\s]+/gi;

/**
 * Redige credencial e contato dentro de um texto que pode conter URL.
 *
 * Origem: #795 — 2026-10-03 14:15 +0200 (OBS-1, OBS-6).
 *
 * Por quê: o PostHog carimba a URL atual em TODO evento (`$current_url`,
 * `$pathname`, `$referrer`, `$initial_*`, `$session_entry_*`) e o autocapture
 * manda o `href` do link clicado (`$elements_chain`, `attr__href`,
 * `$external_click_url`). Com isso o token portador do convite do CRM saía
 * para o PostHog no `$pageview` da chegada, e o `mailto:`/`tel:`/`wa.me` de
 * todo lead clicado ia junto no autocapture. Regex, e não `new URL()`, porque o
 * mesmo texto às vezes é uma URL e às vezes é a string serializada da cadeia de
 * elementos (`a:href="mailto:…"nth-child="1"`), e porque `URL` normaliza a
 * string (barra final, codificação) mesmo quando nada é redigido.
 *
 * Contrato: pura e idempotente (`f(f(x)) === f(x)`). Devolve o MESMO valor
 * quando não há nada a redigir. Só troca valores — nunca remove o parâmetro nem
 * o caminho, para o insight continuar agrupando por rota.
 *
 * Usado por: `redactPostHogCapture` e `telemetryPageLocation` (abaixo);
 * `apps/crm/src/lib/ga4.ts` (`page_path`).
 *
 * Falhas conhecidas:
 *  - 2026-10-03 (#795, OBS-1): token do convite do CRM no `$current_url` e no
 *    `page_location` do GA4.
 *  - 2026-10-03 (#795, OBS-6): `mask_all_element_attributes: true` NÃO tira o
 *    `href` do autocapture — o posthog-js 1.372 o anexa depois, por fora da
 *    máscara (`autocapturePropertiesForElement`). Só esta redação o cobre.
 *
 * Dicas: parâmetro novo com credencial vai em `SENSITIVE_URL_PARAMS`, com a
 * origem dele no comentário; o teste `telemetryRedaction.test.ts` cobre cada
 * forma (crua, codificada, cadeia de elementos).
 *
 * Histórico:
 *  - 2026-10-03 14:15 +0200 — (#795, OBS-1/OBS-6) criado.
 */
export function redactTelemetryText(value: string): string {
  return value
    .replace(PARAM_RE, `$1${TELEMETRY_REDACTED}`)
    .replace(ENCODED_PARAM_RE, `$1${TELEMETRY_REDACTED}`)
    .replace(CONTACT_SCHEME_RE, `$1:${TELEMETRY_REDACTED}`)
    .replace(WHATSAPP_HOST_RE, `$1/${TELEMETRY_REDACTED}`)
    .replace(PAY_PATH_RE, `$1${TELEMETRY_REDACTED}`);
}

/**
 * `page_location` para o GA4: origem + caminho, SEM query e SEM fragmento, com
 * o caminho redigido.
 *
 * Origem: #795 — 2026-10-03 14:15 +0200 (OBS-1).
 *
 * Por quê: o GA4 anexa o `page_location` a todo hit do stream, e o default é
 * `window.location.href` cru. Tirar a query inteira (e não só os parâmetros da
 * lista) é proposital: no GA4 nenhum relatório nosso depende dela, e assim um
 * parâmetro sensível que ainda não está em `SENSITIVE_URL_PARAMS` também não sai.
 *
 * Contrato: `https://crm.x/invite/accept?token=abc#y` → `https://crm.x/invite/accept`.
 *
 * Usado por: `apps/crm/src/lib/ga4.ts` (`trackPageview`).
 *
 * Histórico:
 *  - 2026-10-03 14:15 +0200 — (#795, OBS-1) criado.
 */
export function telemetryPageLocation(href: string): string {
  const withoutQuery = href.split(/[?#]/, 1)[0] ?? '';
  return redactTelemetryText(withoutQuery);
}

/**
 * Forma mínima de um evento do posthog-js (`CaptureResult`) que a redação
 * precisa enxergar. Estrutural para `@etiquetachef/types` não depender do SDK.
 */
export type PostHogCaptureLike = {
  properties: Record<string, unknown>;
  $set?: Record<string, unknown> | undefined;
  $set_once?: Record<string, unknown> | undefined;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Cada elemento da cadeia do autocapture (`attr__href`, `$el_text`, …) — rasa. */
function redactElements(elements: readonly unknown[]): readonly unknown[] {
  let out: unknown[] | null = null;
  for (let index = 0; index < elements.length; index += 1) {
    const element = elements[index];
    if (!isRecord(element)) continue;
    const next = redactRecord(element);
    if (next !== element) {
      out ??= [...elements];
      out[index] = next;
    }
  }
  return out ?? elements;
}

/**
 * `$heatmap_data` é indexado pela URL da página (`{ [href]: pontos[] }`), com
 * a query crua. Duas chaves que viram a mesma depois da redação têm os pontos
 * somados, não sobrescritos.
 */
function redactHeatmapKeys(data: Record<string, unknown>): Record<string, unknown> {
  let changed = false;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const safeKey = redactTelemetryText(key);
    if (safeKey !== key) changed = true;
    const previous = out[safeKey];
    out[safeKey] =
      Array.isArray(previous) && Array.isArray(value) ? [...(previous as unknown[]), ...(value as unknown[])] : value;
  }
  return changed ? out : data;
}

/** Redige um valor: string inteira, ou desce em `$elements` e `$heatmap_data`. */
function redactValue(key: string, value: unknown): unknown {
  if (typeof value === 'string') return redactTelemetryText(value);
  if (key === '$elements' && Array.isArray(value)) return redactElements(value);
  if (key === '$heatmap_data' && isRecord(value)) return redactHeatmapKeys(value);
  return value;
}

/** Cópia-na-escrita: devolve o MESMO objeto quando nada mudou. */
function redactRecord(record: Record<string, unknown>): Record<string, unknown> {
  let out: Record<string, unknown> | null = null;
  for (const key of Object.keys(record)) {
    const value = record[key];
    const next = redactValue(key, value);
    if (next !== value) {
      out ??= { ...record };
      out[key] = next;
    }
  }
  return out ?? record;
}

/**
 * `before_send` do posthog-js: redige credencial e contato de um evento antes
 * de ele sair do navegador.
 *
 * Origem: #795 — 2026-10-03 14:15 +0200 (OBS-1, OBS-6).
 *
 * Por quê: ver `redactTelemetryText`. É o ÚNICO ponto por onde passa todo
 * evento — `$pageview`, `$pageleave`, `$autocapture`, `$identify` (com o
 * `$set_once` dos `$initial_*`), `$exception`, `$$heatmap`, `$snapshot` — e é
 * o que resolve sem depender de cada call site lembrar. O `before_send` não
 * alcança o `href` gravado DENTRO do replay (evento Meta do rrweb); para esse
 * existe `redactReplayNetworkRequest`.
 *
 * Contrato: aceita e devolve `CaptureResult | null` (assinatura do
 * `BeforeSendFn`); `null` passa adiante (outro `before_send` já descartou).
 * Varre as strings de 1º nível de `properties`, `$set` e `$set_once`, os
 * elementos de `$elements` e as chaves de `$heatmap_data`. Não descarta evento
 * e não remove propriedade. Não toca na prop `token` do SDK (a project key
 * `phc_…`): ela não tem forma de URL.
 *
 * Usado por: `posthogBeforeSend`, que é o `before_send` do `posthog.init` em
 * `apps/app`, `apps/admin`, `apps/crm` e `apps/developers`
 * (`src/lib/posthog.ts`) desde a #315. O developers não tem rota com
 * credencial nem link de contato; lá a redação é só rede de segurança.
 *
 * Arquitetura: complementa — não substitui — `mask_all_text` /
 * `mask_all_element_attributes` (admin e CRM) e o `replaceState` do convite
 * (`InviteAcceptPage`). Configuração do projeto PostHog não é tocada: tudo é SDK.
 *
 * Dados — exemplo de entrada (anonimizado, posthog-js 1.372.3):
 *   { "event": "$pageview",
 *     "properties": { "$current_url": "https://crm.etiquetachef.com/invite/accept?token=AbC…",
 *                     "$pathname": "/invite/accept", "token": "phc_…" },
 *     "$set_once": { "$initial_current_url": "https://crm…/invite/accept?token=AbC…" } }
 * saída: os dois `token=AbC…` viram `token=[redacted]`; `"token": "phc_…"` fica.
 *
 * Dicas: o teste de cada app (`src/lib/posthog.test.ts`) prova que o init
 * passa ESTA função; o desta lib prova a redação.
 *
 * Histórico:
 *  - 2026-10-03 14:15 +0200 — (#795, OBS-1/OBS-6) criado.
 *  - 2026-10-13 11:23 +0200 — (#315) os inits passam a chamá-la via
 *    `posthogBeforeSend`, depois do descarte do ruído do navegador.
 */
export function redactPostHogCapture<T extends PostHogCaptureLike>(capture: T | null): T | null {
  if (capture === null) return null;
  const properties = redactRecord(capture.properties);
  const $set = capture.$set === undefined ? undefined : redactRecord(capture.$set);
  const $setOnce = capture.$set_once === undefined ? undefined : redactRecord(capture.$set_once);
  if (properties === capture.properties && $set === capture.$set && $setOnce === capture.$set_once) {
    return capture;
  }
  return {
    ...capture,
    properties,
    ...($set === undefined ? {} : { $set }),
    ...($setOnce === undefined ? {} : { $set_once: $setOnce }),
  };
}

/**
 * `session_recording.maskCapturedNetworkRequestFn` do posthog-js: redige a URL
 * que o REPLAY grava por conta própria.
 *
 * Origem: #795 — 2026-10-03 14:15 +0200 (OBS-1).
 *
 * Por quê: o evento Meta do rrweb guarda `window.location.href` cru dentro de
 * `$snapshot_data` — o `before_send` não desce lá. O posthog-js 1.372 passa
 * esse `href` (e o do `$url_changed`) por esta função antes de gravar
 * (`LazyLoadedSessionRecording._maskUrl`), e é a mesma função que receberia as
 * requisições de rede se a captura de rede fosse ligada.
 *
 * Contrato: devolve uma cópia com `name`/`url` redigidos (o SDK lê `name`,
 * ou `url` na API antiga); devolve o mesmo objeto quando não há o que redigir.
 *
 * Usado por: `apps/app/src/lib/posthog.ts` (único SPA com replay ligado).
 *
 * Histórico:
 *  - 2026-10-03 14:15 +0200 — (#795, OBS-1) criado.
 */
export function redactReplayNetworkRequest<T extends { name?: string | undefined; url?: string | undefined }>(
  request: T,
): T {
  const name = request.name === undefined ? undefined : redactTelemetryText(request.name);
  const url = request.url === undefined ? undefined : redactTelemetryText(request.url);
  if (name === request.name && url === request.url) return request;
  return {
    ...request,
    ...(name === undefined ? {} : { name }),
    ...(url === undefined ? {} : { url }),
  };
}

/**
 * Mensagens de erro que o navegador lança sem que nada tenha quebrado.
 * A lista é fechada: só entra mensagem cuja inocuidade está documentada.
 *
 * `ResizeObserver loop completed with undelivered notifications.` (Chrome) e
 * `ResizeObserver loop limit exceeded` (Chrome antigo e Safari): um layout que
 * reage ao próprio ResizeObserver dentro do mesmo frame. As tabelas do
 * Cloudscape fazem isso. O navegador adia a notificação para o frame seguinte
 * e a tela segue íntegra. O Sentry descarta as duas por padrão. (#315)
 */
const BENIGN_BROWSER_ERRORS: readonly RegExp[] = [
  /^(?:Uncaught )?(?:Error: )?ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/,
];

/**
 * Diz se a mensagem de erro é um aviso inofensivo do navegador, que não deve
 * virar exceção fatal na telemetria.
 *
 * Origem: #315 — 2026-10-13 11:23 +0200 (alerta "Exceção fatal no web"
 * disparado só por ResizeObserver no admin).
 *
 * Por quê: o `window.onerror` entrega esse aviso como exceção não tratada, sem
 * `severity`. Ele passa pelo filtro `severity is_not NON_FATAL` do insight que
 * alimenta o alerta horário. Em 2026-10-13, 100 % dos fatais reais dos últimos
 * 30 dias eram isto: três eventos em `/billing/addons` do admin, um só usuário.
 *
 * Contrato: `true` só para mensagem da lista `BENIGN_BROWSER_ERRORS`. Função
 * pura.
 *
 * Usado por: `dropBenignBrowserException` (via `posthogBeforeSend`, em
 * `apps/app`, `apps/admin`, `apps/crm` e `apps/developers`); handlers `window.error` de `apps/crm/src/main.tsx`
 * e de `apps/developers/src/main.tsx`, que reportam à mão.
 *
 * Dicas: ampliar a lista exige evidência de que a mensagem é inócua, citada no
 * comentário da regex. Nunca entra erro de código nosso: aí a correção é no código.
 */
export function isBenignBrowserError(message: string): boolean {
  return BENIGN_BROWSER_ERRORS.some((re) => re.test(message));
}

/** Mensagens de um `$exception` do posthog-js, de onde quer que o SDK as ponha. */
function exceptionMessages(properties: Record<string, unknown>): string[] {
  const out: string[] = [];
  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const entry of list) {
      if (isRecord(entry) && typeof entry.value === 'string') out.push(entry.value);
    }
  }
  const values = properties.$exception_values;
  if (Array.isArray(values)) {
    for (const v of values) if (typeof v === 'string') out.push(v);
  }
  if (typeof properties.$exception_message === 'string') out.push(properties.$exception_message);
  return out;
}

/**
 * `before_send` do posthog-js: descarta o `$exception` cujas mensagens são,
 * todas, avisos inofensivos do navegador (`isBenignBrowserError`).
 *
 * Origem: #315 — 2026-10-13 11:23 +0200.
 *
 * Por quê: o alerta de insight "Exceção fatal no web"
 * (`scripts/posthog/dashboards/error-tracking.json`) dispara com UM evento por
 * hora. Um aviso de ResizeObserver no admin o fez disparar três vezes sem que
 * nada tivesse quebrado. Descartar na origem limpa ao mesmo tempo o alerta, o
 * dashboard e o Error Tracking. Filtrar no insight deixaria o evento inútil
 * saindo. Configuração do projeto PostHog continua intocada, como na #795.
 *
 * Contrato: devolve `null` (o SDK não envia) só quando o evento é `$exception`
 * e há pelo menos uma mensagem, sendo todas benignas. Exceção mista (benigna
 * mais uma real) passa inteira. Exceção sem mensagem legível também passa,
 * porque na dúvida o alerta toca. Qualquer outro evento passa intacto, e
 * `null` de entrada é repassado.
 *
 * Usado por: `posthogBeforeSend`, sempre antes de `redactPostHogCapture`.
 * Nessa ordem, a redação nem chega a olhar o evento descartado.
 *
 * Dados — `$exception` real que disparou o alerta (anonimizado, posthog-js
 * 1.372.3, capturado em 2026-10-13):
 *   { "event": "$exception",
 *     "properties": {
 *       "$exception_list": [{ "type": "Error",
 *         "value": "ResizeObserver loop completed with undelivered notifications." }],
 *       "$current_url": "https://admin.etiquetachef.com/billing/addons",
 *       "surface": "web-admin" } }      // sem `severity`: veio do `window.onerror`
 * `$exception_values` e `$exception_message` são lidos também, por segurança
 * contra mudança de formato no SDK.
 *
 * Dicas: o teste desta lib prova o descarte. O de cada app
 * (`src/lib/posthog.test.ts`) prova que o init usa `posthogBeforeSend`.
 *
 * Histórico:
 *  - 2026-10-13 11:23 +0200 — (#315) criado.
 */
export function dropBenignBrowserException<T extends PostHogCaptureLike & { event: string }>(
  capture: T | null,
): T | null {
  if (capture === null || capture.event !== '$exception') return capture;
  const messages = exceptionMessages(capture.properties);
  if (messages.length > 0 && messages.every(isBenignBrowserError)) return null;
  return capture;
}

/**
 * O `before_send` único dos SPAs web: descarta o ruído do navegador e redige
 * o resto.
 *
 * Origem: #315 — 2026-10-13 11:23 +0200.
 *
 * Por quê: os inits passavam `redactPostHogCapture` direto. Compor aqui, e não
 * num array em cada app, mantém um ponto só: um app novo que copie o init
 * leva o filtro junto, e os testes dos apps seguem chamando `before_send`
 * como função.
 *
 * Contrato: `redactPostHogCapture(dropBenignBrowserException(capture))`.
 *
 * Usado por: `before_send` do `posthog.init` em `apps/app`, `apps/admin`,
 * `apps/crm` e `apps/developers` (`src/lib/posthog.ts`), e o
 * `dropIfMainCaptures` da instância `errors` do `apps/app`.
 *
 * Falhas conhecidas:
 *  - 2026-10-13 (#315): a primeira versão da correção esqueceu o
 *    `apps/developers`, que tinha `capture_exceptions` sem `before_send`
 *    nenhum. Init novo de posthog-js tem de usar esta função.
 *
 * Histórico:
 *  - 2026-10-13 11:23 +0200 — (#315) criado.
 *  - 2026-10-13 11:44 +0200 — (#315) `apps/developers` passa a usar.
 */
export function posthogBeforeSend<T extends PostHogCaptureLike & { event: string }>(
  capture: T | null,
): T | null {
  return redactPostHogCapture(dropBenignBrowserException(capture));
}
